import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import RemoveIcon from "@mui/icons-material/Remove";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { borderRadiusPx } from "../../../../../theme";

import { toast } from "../../../../Components/ui";
import {
  // THREE_SLOT_CONFIG,
  JACKPOT_DEFAULTS,
  JACKPOT_TIER_THEMES,
  SLOT_MACHINE_FRAME_THEME,
  DEFAULT_LINE_MULTIPLIERS,
  DEFAULT_TOTAL_LINES,
  getThreeSlotPaylines,
  getSymbolsByCategory,
  getRandomSymbol,
  mapContractIconsToGrid,
  evaluateThreeSlotSpin,
} from "./threeSlotConfig";
import threeSlotBg from "../../../../../static/Images/newlyAdded/3_slot_game.png";
import ThreeSlotSymbolIcon from "./ThreeSlotSymbolIcon";
import ThreeSlotPaytableModal from "./ThreeSlotPaytableModal";
import ThreeSlotDepositModal from "./ThreeSlotDepositModal";
import ThreeSlotWinModal from "./ThreeSlotWinModal";
import FailedPopup from "../../../../Components/FailedPopup";
import { slotAudio } from "./slotAudio";

export const ThreeSlotMachineDetailCard = ({
  lotteryData,
  account, onSpin, spinLoading = false, lastSpinResult,
  balance: parentBalance,
  win: parentWin,
  setBalance: setParentBalance,
  setWin: setParentWin,
}) => {
  const category =
    lotteryData?.slotCategory ||
    (lotteryData?.name?.toLowerCase().includes("crypto") ? "crypto" : "fiat");
  const currency = lotteryData?.currency || "BET";
  const activeSymbols = getSymbolsByCategory(category);

  // Game States: User BET balance from wallet
  const [balance, setBalance] = useState(() => {
    const init = parseFloat(parentBalance);
    return !isNaN(init) ? init.toFixed(2) : "0.00";
  });
  const [isSpinning, setIsSpinning] = useState(false);

  // Sync when parentBalance from wallet updates
  useEffect(() => {
    if (!isSpinning && parentBalance !== undefined && parentBalance !== null) {
      setBalance(parentBalance);
    }
  }, [parentBalance, isSpinning]);

  const [currentWin, setCurrentWin] = useState("0.00");

  // --- MACHINE BET BOUNDS (FROM CONTRACT LOTTERY DATA) ---
  const rawMin = parseFloat(lotteryData?.minBet || lotteryData?.ticketPrice);
  const minBet = !isNaN(rawMin) && rawMin > 0 ? rawMin : 1.0;

  const rawMax = parseFloat(lotteryData?.maxBet);
  const hasContractMax = !isNaN(rawMax) && rawMax >= minBet;
  const maxBet = hasContractMax ? rawMax : Math.max(minBet * 10, 100.0);
  const isBetFixed = maxBet <= minBet;

  // Step size for +/- controls (e.g. 10 -> step 5; 1 -> step 1; 0.5 -> step 0.1)
  const stepSize = minBet >= 10 ? 5 : minBet >= 1 ? 1 : 0.1;

  // Player chosen Bet: starts at minBet and does NOT update when lines are selected!
  // Can be fixed or adjusted by user input based on max bet available.
  const [betAmount, setBetAmount] = useState(() => minBet);
  const [betInputText, setBetInputText] = useState(() => String(minBet));

  // Sync bet limits and values when lotteryData loads or changes
  useEffect(() => {
    if (lotteryData) {
      const min = parseFloat(lotteryData?.minBet || lotteryData?.ticketPrice);
      const validMin = !isNaN(min) && min > 0 ? min : 1.0;
      const max = parseFloat(lotteryData?.maxBet);
      const validMax = !isNaN(max) && max >= validMin ? max : Math.max(validMin * 10, 100.0);

      setBetAmount((prev) => {
        if (prev < validMin) return validMin;
        if (prev > validMax) return validMax;
        return prev;
      });
      setBetInputText((prev) => {
        const num = parseFloat(prev);
        if (isNaN(num) || num < validMin) return String(validMin);
        if (num > validMax) return String(validMax);
        return prev;
      });
    }
  }, [lotteryData]);

  // --- SCALABLE PAYLINE ARCHITECTURE & DYNAMIC MULTIPLIERS ---
  // Paylines and multipliers dynamically scale from contract/lotteryData with fallback to fixed patterns
  const activePaylines = useMemo(() => {
    if (Array.isArray(lotteryData?.paylines) && lotteryData.paylines.length > 0) {
      return lotteryData.paylines;
    }
    const mults = lotteryData?.lineMultipliers || DEFAULT_LINE_MULTIPLIERS;
    const total =
      lotteryData?.totalLines ||
      lotteryData?.availableLines ||
      (Array.isArray(mults) ? mults.length : DEFAULT_TOTAL_LINES);
    return getThreeSlotPaylines(mults, total);
  }, [
    lotteryData?.paylines,
    lotteryData?.lineMultipliers,
    lotteryData?.totalLines,
    lotteryData?.availableLines,
  ]);

  // Player chooses active paylines (default: all available lines active)
  const [selectedLines, setSelectedLines] = useState(() =>
    activePaylines.map((p) => p.id)
  );

  // Sync selectedLines when contract or game data updates available paylines
  useEffect(() => {
    const validIds = activePaylines.map((p) => p.id);
    setSelectedLines((prev) => {
      const next = prev.filter((id) => validIds.includes(id));
      return next.length > 0 ? next : validIds;
    });
  }, [activePaylines]);

  const [hoveredLine, setHoveredLine] = useState(null);
  const [winningLineResults, setWinningLineResults] = useState([]);
  const [missedLineResults, setMissedLineResults] = useState([]);

  // Total Spin Bet: stays at chosen betAmount (does NOT update based on lines selected!)
  const totalBetAmount = Number(betAmount.toFixed(2));

  // Line Contribution: The total bet is divided equally among the active selected lines
  // e.g. 10 BET total with 5 lines = 2.00 BET / line; with 1 line = 10.00 BET on that 1 line!
  const activeLineCount = Math.max(1, selectedLines.length);
  const betPerLine = Number((totalBetAmount / activeLineCount).toFixed(4));

  // Handlers for bet adjustments
  const handleBetInputChange = (e) => {
    if (isBetFixed) return;
    const val = e.target.value;
    setBetInputText(val);
    const parsed = parseFloat(val);
    if (!isNaN(parsed) && parsed >= minBet && parsed <= maxBet) {
      setBetAmount(Number(parsed.toFixed(2)));
    }
  };

  const handleBetInputBlur = () => {
    if (isBetFixed) return;
    let parsed = parseFloat(betInputText);
    if (isNaN(parsed) || parsed < minBet) {
      parsed = minBet;
    } else if (parsed > maxBet) {
      parsed = maxBet;
    }
    const clean = Number(parsed.toFixed(2));
    setBetAmount(clean);
    setBetInputText(String(clean));
  };

  const handleDecreaseBet = () => {
    if (isBetFixed) return;
    slotAudio.playClick();
    setBetAmount((prev) => {
      const next = Math.max(minBet, Number((prev - stepSize).toFixed(2)));
      setBetInputText(String(next));
      return next;
    });
  };

  const handleIncreaseBet = () => {
    if (isBetFixed) return;
    slotAudio.playClick();
    setBetAmount((prev) => {
      const next = Math.min(maxBet, Number((prev + stepSize).toFixed(2)));
      setBetInputText(String(next));
      return next;
    });
  };

  const handleSetMinBet = () => {
    if (isBetFixed) return;
    slotAudio.playClick();
    setBetAmount(minBet);
    setBetInputText(String(minBet));
  };

  const handleSetMaxBet = () => {
    if (isBetFixed) return;
    slotAudio.playClick();
    setBetAmount(maxBet);
    setBetInputText(String(maxBet));
  };

  // Payline toggle handlers
  const handleToggleLine = (lineId) => {
    slotAudio.playClick();
    setSelectedLines((prev) => {
      if (prev.includes(lineId)) {
        if (prev.length <= 1) {
          toast.info("At least 1 payline must be active!", { autoClose: 1500 });
          return prev;
        }
        return prev.filter((id) => id !== lineId);
      } else {
        return [...prev, lineId].sort((a, b) => a - b);
      }
    });
  };

  const handleSelectSingleLine = () => {
    slotAudio.playClick();
    setSelectedLines([1]);
    toast.info("Line 1 Activated — 100% Bet Concentrated on Line 1!", { autoClose: 1400 });
  };

  const handleSelectAllLines = () => {
    slotAudio.playClick();
    setSelectedLines(activePaylines.map((p) => p.id));
    toast.success(`All ${activePaylines.length} Paylines Activated — Max Coverage!`, { autoClose: 1400 });
  };

  // Helper: check if a symbol position (reelIdx, rowIdx) is part of any winning line
  const isSymbolOnWinningLine = (reelIdx, rowIdx) => {
    if (isSpinning || !winningLineResults || winningLineResults.length === 0) return false;
    return winningLineResults.some((wl) =>
      wl.payline?.coords?.some(([r, c]) => r === reelIdx && c === rowIdx)
    );
  };

  // 3-Reel Symbol Display State: Each reel has [topSymbol, mainSymbol, bottomSymbol]
  const [reelDisplay, setReelDisplay] = useState([
    [activeSymbols[0], activeSymbols[1], activeSymbols[2]],
    [activeSymbols[2], activeSymbols[3], activeSymbols[4]],
    [activeSymbols[4], activeSymbols[0], activeSymbols[1]],
  ]);

  const [spinningReels, setSpinningReels] = useState([false, false, false]);
  const [isAutoSpinning, setIsAutoSpinning] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  // Bonus & Free Spins (UI commented out)
  const [freeSpinsRemaining, setFreeSpinsRemaining] = useState(0);
  // const [totalFreeSpins, setTotalFreeSpins] = useState(10);

  // Jackpots (Mega, Major, Mini)
  const [jackpots, setJackpots] = useState({
    mega: parseFloat(lotteryData?.jackpots?.mega?.amount) || JACKPOT_DEFAULTS.mega,
    major: parseFloat(lotteryData?.jackpots?.major?.amount) || JACKPOT_DEFAULTS.major,
    mini: parseFloat(lotteryData?.jackpots?.mini?.amount) || JACKPOT_DEFAULTS.mini,
  });

  // Sync local jackpots when parent fetches live blockchain data
  useEffect(() => {
    if (lotteryData?.jackpots && !isNaN(parseFloat(lotteryData.jackpots.mini.amount))) {
      setJackpots({
        mega: parseFloat(lotteryData.jackpots.mega.amount),
        major: parseFloat(lotteryData.jackpots.major.amount),
        mini: parseFloat(lotteryData.jackpots.mini.amount),
      });
    }
  }, [lotteryData?.jackpots]);

  // Modals
  const [paytableOpen, setPaytableOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const [winModalOpen, setWinModalOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Failed / Error Popup State
  const [failedModal, setFailedModal] = useState({
    open: false,
    title: "Insufficient Balance",
    message: "You don't have enough BET tokens to place this bet.",
    description: "Please deposit or acquire BET tokens in your wallet before spinning.",
  });

  // Sync parent balance if provided
  useEffect(() => {
    if (setParentBalance) setParentBalance(balance);
  }, [balance, setParentBalance]);

  useEffect(() => {
    if (setParentWin) setParentWin(currentWin);
  }, [currentWin, setParentWin]);

  // Jackpot formatting (updated only and only from contract)
  const formatJackpot = (val) => {
    const num = Number(val);
    if (isNaN(num)) return "0.00";
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Quick Bet Adjusters - Commented out since bet is fixed
  // const handleDecreaseBet = () => {
  //   slotAudio.playClick();
  //   if (betIndex > 0) {
  //     setBetIndex((prev) => prev - 1);
  //   }
  // };
  //
  // const handleIncreaseBet = () => {
  //   slotAudio.playClick();
  //   if (betIndex < THREE_SLOT_CONFIG.betSteps.length - 1) {
  //     setBetIndex((prev) => prev + 1);
  //   }
  // };

  const handleToggleSound = () => {
    const muted = slotAudio.toggleMute();
    setIsMuted(muted);
    toast.info(muted ? "Sound Muted" : "Sound Enabled", { autoClose: 1200 });
  };


  const spinIntervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (spinIntervalRef.current) clearInterval(spinIntervalRef.current);
    };
  }, []);

  // Main Spin Logic
  const handleSpin = useCallback(async () => {
    if (isSpinning || spinLoading) return;

    const currentBal = parseFloat(balance);
    const isFreeSpin = freeSpinsRemaining > 0;

    if (!isFreeSpin && currentBal < totalBetAmount) {
      setIsAutoSpinning(false);
      setFailedModal({
        open: true,
        title: "Insufficient Balance",
        message: `Your balance is ${parseFloat(balance || 0).toFixed(2)} BET, but this game requires ${totalBetAmount.toFixed(2)} BET.`,
        description: "Please deposit or acquire BET tokens in your wallet before spinning.",
      });
      return;
    }

    let hasStartedSpinning = false;
    const startReelRotation = () => {
      if (hasStartedSpinning) return;
      hasStartedSpinning = true;

      // Deduct bet if not free spin
      if (!isFreeSpin) {
        setBalance((prev) => (parseFloat(prev) - totalBetAmount).toFixed(2));
      } else {
        setFreeSpinsRemaining((prev) => prev - 1);
      }

      setIsSpinning(true);
      setSpinningReels([true, true, true]);
      setCurrentWin("0.00");
      setLastResult(null);
      setWinningLineResults([]);
      setMissedLineResults([]);
      setWinModalOpen(false);

      // Start rapid rotation interval immediately upon spin trigger
      if (spinIntervalRef.current) clearInterval(spinIntervalRef.current);
      spinIntervalRef.current = setInterval(() => {
        slotAudio.playSpinTick();
        setReelDisplay([
          [getRandomSymbol(category), getRandomSymbol(category), getRandomSymbol(category)],
          [getRandomSymbol(category), getRandomSymbol(category), getRandomSymbol(category)],
          [getRandomSymbol(category), getRandomSymbol(category), getRandomSymbol(category)],
        ]);
      }, 90);
    };

    let chainSpinResult = null;

    if (onSpin && typeof onSpin === "function" && !isFreeSpin) {
      chainSpinResult = await onSpin(lotteryData, totalBetAmount, startReelRotation);

      if (!chainSpinResult) {
        setIsAutoSpinning(false);
        if (hasStartedSpinning) {
          if (spinIntervalRef.current) clearInterval(spinIntervalRef.current);
          setSpinningReels([false, false, false]);
          setIsSpinning(false);
        }
        return;
      }
    } else {
      // Free spins or offline demo mode: start immediately
      startReelRotation();
    }

    // Ensure reels are rotating in case onTxConfirmed didn't fire
    if (!hasStartedSpinning) {
      startReelRotation();
    }

    // Extract contract 9-icon places or legacy 3-reel positions
    let rawResultIcons = null;
    if (chainSpinResult?.grid) {
      rawResultIcons = chainSpinResult.grid;
    } else if (chainSpinResult?.reels9) {
      rawResultIcons = chainSpinResult.reels9;
    } else if (chainSpinResult?.icons) {
      rawResultIcons = chainSpinResult.icons;
    } else if (Array.isArray(chainSpinResult?.reels)) {
      rawResultIcons = chainSpinResult.reels;
    }

    // Generate full 3x3 final reels [top, center, bottom] = 9 symbol positions
    const finalReels = mapContractIconsToGrid(rawResultIcons, category);

    // Step 1: Stop Reel 1 (650ms from result arrival)
    setTimeout(() => {
      slotAudio.playReelStop();
      setSpinningReels([false, true, true]);
      setReelDisplay((prev) => [finalReels[0], prev[1], prev[2]]);
    }, 650);

    // Step 2: Stop Reel 2 (1250ms from result arrival)
    setTimeout(() => {
      slotAudio.playReelStop();
      setSpinningReels([false, false, true]);
      setReelDisplay((prev) => [finalReels[0], finalReels[1], prev[2]]);
    }, 1250);

    // Step 3: Stop Reel 3 (1850ms from result arrival) & evaluate all 5 paylines
    setTimeout(() => {
      slotAudio.playReelStop();
      if (spinIntervalRef.current) clearInterval(spinIntervalRef.current);
      setSpinningReels([false, false, false]);
      setReelDisplay(finalReels);
      setIsSpinning(false);

      // Evaluate paylines against player's active line selection with dynamic activePaylines
      const outcome = evaluateThreeSlotSpin(
        finalReels,
        selectedLines,
        betPerLine,
        isFreeSpin,
        jackpots,
        totalBetAmount,
        activePaylines
      );

      outcome.betAmount = totalBetAmount;
      outcome.betPerLine = betPerLine;
      outcome.requestId = chainSpinResult?.requestId;

      setWinningLineResults(outcome.winningLines);
      setMissedLineResults(outcome.missedLines);
      setLastResult(outcome);

      if (outcome.isWin) {
        if (outcome.jackpotTier) {
          slotAudio.playJackpot();
        } else {
          slotAudio.playWin();
        }

        const winFormatted = outcome.totalWin.toFixed(2);
        setCurrentWin(winFormatted);
        setBalance((prev) => (parseFloat(prev) + outcome.totalWin).toFixed(2));

        if (outcome.freeSpinsWon > 0) {
          setFreeSpinsRemaining((prev) => prev + outcome.freeSpinsWon);
        }
      } else {
        slotAudio.playLose();
      }

      // Show win/loss popup modal
      setWinModalOpen(true);
    }, 1850);
  }, [
    isSpinning,
    spinLoading,
    balance,
    totalBetAmount,
    betPerLine,
    selectedLines,
    freeSpinsRemaining,
    category,
    jackpots,
    lotteryData,
    onSpin,
    activeSymbols,
    activePaylines,
  ]);


  const spinRef = useRef(handleSpin);
  useEffect(() => {
    spinRef.current = handleSpin;
  }, [handleSpin]);

  // Autoplay handler
  useEffect(() => {
    let timer;
    if (isAutoSpinning && !isSpinning) {
      timer = setTimeout(() => {
        if (isAutoSpinning) {
          spinRef.current();
        }
      }, 1200);
    }
    return () => clearTimeout(timer);
  }, [isAutoSpinning, isSpinning]);

  const handleToggleAutoPlay = () => {
    slotAudio.playClick();
    if (isAutoSpinning) {
      setIsAutoSpinning(false);
      toast.info("Autoplay Stopped", { autoClose: 1500 });
    } else {
      setIsAutoSpinning(true);
      toast.success("Autoplay Started!", { autoClose: 1500 });
      if (!isSpinning) {
        handleSpin();
      }
    }
  };

  return (
    <Box
      className="slot-game-app pokies-game-app"
      sx={{
        width: "100%",
        maxWidth: "100%",
        margin: "0 auto",
        bgcolor: SLOT_MACHINE_FRAME_THEME.bg,
        borderRadius: SLOT_MACHINE_FRAME_THEME.borderRadius,
        boxShadow: SLOT_MACHINE_FRAME_THEME.boxShadow,
        overflow: "hidden",
        position: "relative",
        border: SLOT_MACHINE_FRAME_THEME.border,
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* Platform Consistent Top Accent Gradient Bar (3-Slot Signature Purple & Gold) */}
      <Box
        sx={{
          height: 4,
          background: "linear-gradient(90deg, #8b5cf6 0%, #a855f7 40%, #eab308 100%)",
        }}
      />

      {/* 1. TOP APP HEADER BAR */}
      <Box
        className="slot-header-bar pokies-header-bar"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2, sm: 2.5 },
          py: 1.25,
          bgcolor: SLOT_MACHINE_FRAME_THEME.headerBg,
          borderBottom: "1px solid #e2e8f0",
          zIndex: 10,
        }}
      >
        {/* Left: 3-SlotBET Game Badge */}
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: borderRadiusPx.md || "8px",
                bgcolor: "#faf5ff",
                color: "#8b5cf6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #e9d5ff",
                fontSize: "1.1rem",
              }}
            >
              🎰
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "0.95rem",
                  fontWeight: 800,
                  color: "#0f172a",
                  lineHeight: 1.1,
                  letterSpacing: "-0.2px",
                }}
              >
                {lotteryData?.name || "3-SlotBET"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  color: "#8b5cf6",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  lineHeight: 1,
                  mt: "2px",
                  textTransform: "uppercase",
                }}
              >
                {category === "crypto" ? "Crypto Slot • 3-Reel Edition" : "Classic Slot • 3-Reel Edition"}
              </Typography>
            </Box>
          </Box>
        </Stack>

        {/* Right: Sound Toggle */}
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <IconButton
            size="small"
            onClick={handleToggleSound}
            sx={{
              color: "#64748b",
              p: 0.75,
              bgcolor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              "&:hover": { color: "#0f172a", bgcolor: "#f1f5f9" },
            }}
          >
            {isMuted ? <VolumeOffIcon sx={{ fontSize: 20 }} /> : <VolumeUpIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        </Stack>
      </Box>

      {/* 2. GAME STAGE (Clean Light Atmosphere consistent with platform cards) */}
      <Box
        className="slot-stage-wrapper pokies-stage-wrapper"
        sx={{
          position: "relative",
          backgroundImage: `url(${threeSlotBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          px: { xs: 1.5, sm: 3 },
          pt: { xs: 2, sm: 2.5 },
          pb: { xs: 2.5, sm: 3.5 },
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.94) 0%, rgba(248, 250, 252, 0.90) 50%, rgba(241, 245, 249, 0.95) 100%)",
            pointerEvents: "none",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 50% 0%, rgba(234, 179, 8, 0.08) 0%, transparent 60%), radial-gradient(circle at 10% 40%, rgba(139, 92, 246, 0.06) 0%, transparent 50%), radial-gradient(circle at 90% 40%, rgba(59, 130, 246, 0.06) 0%, transparent 50%)",
            pointerEvents: "none",
          },
        }}
      >
        <Box sx={{ position: "relative", zIndex: 2 }}>
          {/* 3. PROGRESSIVE JACKPOT TIERS (MEGA, MAJOR, MINI) */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: { xs: 0.75, sm: 1.5 },
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            {/* MEGA JACKPOT */}
            <Box
              sx={{
                p: { xs: 0.75, sm: 1.25 },
                borderRadius: JACKPOT_TIER_THEMES.mega.borderRadius,
                background: JACKPOT_TIER_THEMES.mega.background,
                border: JACKPOT_TIER_THEMES.mega.border,
                boxShadow: JACKPOT_TIER_THEMES.mega.boxShadow,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.72rem" },
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.mega.titleColor,
                  letterSpacing: "0.5px",
                  lineHeight: 1,
                }}
              >
                {JACKPOT_TIER_THEMES.mega.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "1.05rem" },
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.mega.valueColor,
                  lineHeight: 1.2,
                  fontFamily: "monospace",
                  mt: 0.25,
                }}
              >
                {currency} {formatJackpot(jackpots.mega)}
              </Typography>
            </Box>

            {/* MAJOR JACKPOT */}
            <Box
              sx={{
                p: { xs: 0.75, sm: 1.25 },
                borderRadius: JACKPOT_TIER_THEMES.major.borderRadius,
                background: JACKPOT_TIER_THEMES.major.background,
                border: JACKPOT_TIER_THEMES.major.border,
                boxShadow: JACKPOT_TIER_THEMES.major.boxShadow,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.72rem" },
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.major.titleColor,
                  letterSpacing: "0.5px",
                  lineHeight: 1,
                }}
              >
                {JACKPOT_TIER_THEMES.major.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "1.05rem" },
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.major.valueColor,
                  lineHeight: 1.2,
                  fontFamily: "monospace",
                  mt: 0.25,
                }}
              >
                {currency} {formatJackpot(jackpots.major)}
              </Typography>
            </Box>

            {/* MINI JACKPOT */}
            <Box
              sx={{
                p: { xs: 0.75, sm: 1.25 },
                borderRadius: JACKPOT_TIER_THEMES.mini.borderRadius,
                background: JACKPOT_TIER_THEMES.mini.background,
                border: JACKPOT_TIER_THEMES.mini.border,
                boxShadow: JACKPOT_TIER_THEMES.mini.boxShadow,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.72rem" },
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.mini.titleColor,
                  letterSpacing: "0.5px",
                  lineHeight: 1,
                }}
              >
                {JACKPOT_TIER_THEMES.mini.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "1.05rem" },
                  fontWeight: 900,
                  color: JACKPOT_TIER_THEMES.mini.valueColor,
                  lineHeight: 1.2,
                  fontFamily: "monospace",
                  mt: 0.25,
                }}
              >
                {currency} {formatJackpot(jackpots.mini)}
              </Typography>
            </Box>
          </Box>

          {/* 4. REELS CASINO FRAME & SIDE PAYLINE INDICATOR PILLARS */}
          <Box
            className="slot-reels-container pokies-reels-container"
            sx={{
              position: "relative",
              borderRadius: "16px",
              background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 50%, #6d28d9 100%)",
              p: "3px",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
              mb: 1.75,
            }}
          >
            <Box
              sx={{
                borderRadius: "12px",
                bgcolor: "#ffffff",
                border: "1.5px solid #e2e8f0",
                display: "flex",
                position: "relative",
                overflow: "hidden",
                boxShadow: "inset 0 2px 6px rgba(0, 0, 0, 0.03)",
              }}
            >
              {/* LEFT SIDE PAYLINE INDICATOR STRIP (INTERACTIVE LINE 1..5 BADGES) */}
              <Box
                sx={{
                  width: { xs: 32, sm: 40 },
                  bgcolor: "#f8fafc",
                  borderRight: "1px solid #e2e8f0",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-around",
                  py: 1.5,
                  alignItems: "center",
                  zIndex: 8,
                }}
              >
                {activePaylines.map((line) => {
                  const isSelected = selectedLines.includes(line.id);
                  const isHovered = hoveredLine === line.id;
                  const isWinning = winningLineResults.some((wl) => wl.lineId === line.id);

                  return (
                    <Box
                      key={`l-${line.id}`}
                      onClick={() => handleToggleLine(line.id)}
                      onMouseEnter={() => setHoveredLine(line.id)}
                      onMouseLeave={() => setHoveredLine(null)}
                      title={`${line.name} (${line.multiplier}x Multiplier): ${line.where}. Click to toggle.`}
                      sx={{
                        width: { xs: 26, sm: 34 },
                        height: { xs: 26, sm: 34 },
                        borderRadius: "8px",
                        bgcolor: isWinning
                          ? "#eab308"
                          : isSelected
                          ? line.badgeColor
                          : "#e2e8f0",
                        color: isSelected || isWinning ? "#ffffff" : "#94a3b8",
                        border: isWinning
                          ? "2px solid #fef08a"
                          : isSelected
                          ? "1.5px solid #ffffff"
                          : "1px dashed #cbd5e1",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        boxShadow: isWinning
                          ? "0 0 14px rgba(234, 179, 8, 0.9)"
                          : isSelected
                          ? `0 2px 8px ${line.color}55`
                          : "none",
                        transform: isHovered || isWinning ? "scale(1.15)" : "scale(1)",
                        transition: "all 0.15s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                        opacity: isSelected ? 1 : 0.45,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: "0.62rem", sm: "0.72rem" },
                          fontWeight: 900,
                          lineHeight: 1,
                        }}
                      >
                        {line.lineNum}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: "0.48rem", sm: "0.55rem" },
                          fontWeight: 800,
                          lineHeight: 1,
                          opacity: 0.95,
                          mt: "1px",
                        }}
                      >
                        {line.multiplier}x
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* 3 SLOT REELS GRID WITH SVG PAYLINE OVERLAY */}
              <Box
                sx={{
                  flex: 1,
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "0px",
                  bgcolor: "#ffffff",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* INTERACTIVE SVG PAYLINE TRACKS & GLOWING WIN LASER OVERLAY */}
                <svg
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                    zIndex: 7,
                  }}
                  viewBox="0 0 300 300"
                  preserveAspectRatio="none"
                >
                  <defs>
                    {activePaylines.map((line) => (
                      <filter
                        key={`glow-filter-${line.id}`}
                        id={`glow-filter-${line.id}`}
                        x="-30%"
                        y="-30%"
                        width="160%"
                        height="160%"
                      >
                        <feGaussianBlur stdDeviation="3.5" result="blur" />
                        <feComposite in="SourceGraphic" in2="blur" operator="over" />
                      </filter>
                    ))}
                  </defs>

                  {activePaylines.map((line) => {
                    const isSelected = selectedLines.includes(line.id);
                    const isHovered = hoveredLine === line.id;
                    const isWinning = winningLineResults.some((wl) => wl.lineId === line.id);
                    const isMissed = missedLineResults.some((ml) => ml.lineId === line.id);

                    // Skip unselected lines that are neither hovered nor won/missed
                    if (!isSelected && !isHovered && !isWinning && !isMissed) return null;

                    // Compute path coordinates across 300x300 viewBox using fixed pattern svgPath
                    let d = line.svgPath || "";
                    if (!d) {
                      if (line.id === 1) d = "M 0 150 L 300 150"; // Middle row
                      else if (line.id === 2) d = "M 0 54 L 300 54"; // Top row
                      else if (line.id === 3) d = "M 0 246 L 300 246"; // Bottom row
                      else if (line.id === 4) d = "M 5 20 L 50 54 L 150 150 L 250 246 L 295 280"; // Diagonal Down
                      else if (line.id === 5) d = "M 5 280 L 50 246 L 150 150 L 250 54 L 295 20"; // Diagonal Up
                    }

                    const strokeColor = isWinning
                      ? "#facc15"
                      : isHovered
                      ? line.color
                      : isMissed
                      ? "#94a3b8"
                      : line.color;

                    const strokeWidth = isWinning ? 4.5 : isHovered ? 3.5 : 2;
                    const strokeOpacity = isWinning ? 1 : isHovered ? 1 : isSelected ? 0.6 : 0.3;
                    const strokeDash = isMissed ? "6 6" : isWinning ? "none" : isSelected ? "none" : "4 4";

                    return (
                      <g key={`svg-payline-${line.id}`}>
                        {(isWinning || isHovered) && (
                          <path
                            d={d}
                            fill="none"
                            stroke={line.color}
                            strokeWidth={strokeWidth + 6}
                            strokeOpacity={0.4}
                            filter={`url(#glow-filter-${line.id})`}
                            strokeLinecap="round"
                          />
                        )}
                        <path
                          d={d}
                          fill="none"
                          stroke={strokeColor}
                          strokeWidth={strokeWidth}
                          strokeOpacity={strokeOpacity}
                          strokeDasharray={strokeDash}
                          strokeLinecap="round"
                          filter={isWinning ? `url(#glow-filter-${line.id})` : undefined}
                          style={{
                            transition: "all 0.2s ease",
                          }}
                        />
                        {/* Glowing connector nodes at the 3 symbol centers for winning lines */}
                        {isWinning &&
                          line.coords.map(([rIdx, rowIdx], i) => {
                            const cx = rIdx === 0 ? 50 : rIdx === 1 ? 150 : 250;
                            const cy = rowIdx === 0 ? 54 : rowIdx === 1 ? 150 : 246;
                            return (
                              <circle
                                key={`win-node-${line.id}-${i}`}
                                cx={cx}
                                cy={cy}
                                r="7"
                                fill="#ffffff"
                                stroke={line.color}
                                strokeWidth="3"
                                filter={`url(#glow-filter-${line.id})`}
                              />
                            );
                          })}
                      </g>
                    );
                  })}
                </svg>

                {/* 3 REEL COLUMNS (SEAMLESS 3D MECHANICAL ROLLER DRUM CYLINDERS) */}
                {[0, 1, 2].map((reelIdx) => {
                  const isThisReelSpinning = spinningReels[reelIdx];
                  const reelSymbols = reelDisplay[reelIdx];

                  return (
                    <Box
                      key={`reel-${reelIdx}`}
                      className={`roller-drum-wheel ${isThisReelSpinning ? "is-rolling" : ""}`}
                      sx={{
                        position: "relative",
                        bgcolor: "#f1f5f9",
                        background:
                          "linear-gradient(90deg, rgba(100, 116, 139, 0.22) 0%, rgba(241, 245, 249, 0.6) 6%, transparent 16%, transparent 84%, rgba(241, 245, 249, 0.6) 94%, rgba(100, 116, 139, 0.22) 100%), linear-gradient(180deg, #94a3b8 0%, #cbd5e1 7%, #e2e8f0 16%, #f8fafc 32%, #ffffff 50%, #f8fafc 68%, #e2e8f0 84%, #cbd5e1 93%, #94a3b8 100%)",
                        borderRight: reelIdx < 2 ? "2px solid #cbd5e1" : "none",
                        boxShadow: reelIdx < 2
                          ? "inset -2px 0 4px rgba(0,0,0,0.08), 1px 0 0 rgba(255,255,255,0.8)"
                          : "none",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-around",
                        py: { xs: 1, sm: 1.5 },
                        minHeight: { xs: 350, sm: 410, md: 440 },
                        overflow: "hidden",
                      }}
                    >
                      {/* Top Curved Roller Hood Vignette Shadow */}
                      <Box
                        className="roller-top-vignette"
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: { xs: 65, sm: 80 },
                          background:
                            "linear-gradient(180deg, rgba(15, 23, 42, 0.5) 0%, rgba(15, 23, 42, 0.25) 35%, rgba(15, 23, 42, 0.05) 70%, transparent 100%)",
                          pointerEvents: "none",
                          zIndex: 6,
                        }}
                      />

                      {/* Bottom Curved Roller Hood Vignette Shadow */}
                      <Box
                        className="roller-bottom-vignette"
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: { xs: 65, sm: 80 },
                          background:
                            "linear-gradient(0deg, rgba(15, 23, 42, 0.5) 0%, rgba(15, 23, 42, 0.25) 35%, rgba(15, 23, 42, 0.05) 70%, transparent 100%)",
                          pointerEvents: "none",
                          zIndex: 6,
                        }}
                      />

                      {/* Center Cylinder Horizontal Gloss Sheen */}
                      <Box
                        className="roller-center-gloss"
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: 0,
                          right: 0,
                          height: { xs: 110, sm: 135 },
                          transform: "translateY(-50%)",
                          background:
                            "linear-gradient(180deg, transparent 0%, rgba(255, 255, 255, 0.2) 30%, rgba(255, 255, 255, 0.45) 50%, rgba(255, 255, 255, 0.2) 70%, transparent 100%)",
                          pointerEvents: "none",
                          zIndex: 4,
                        }}
                      />

                      {/* Top Symbol (Row 0 - Curved Upper Cylinder Slope) */}
                      <Box
                        sx={{
                          opacity: 0.9,
                          transform: "perspective(600px) rotateX(15deg) scale(0.9)",
                          transformOrigin: "bottom center",
                          filter: isThisReelSpinning ? "blur(3px)" : "none",
                          transition: "filter 0.15s ease",
                          zIndex: 5,
                        }}
                      >
                        <ThreeSlotSymbolIcon
                          symbol={reelSymbols[0]}
                          size="md"
                          isWinning={isSymbolOnWinningLine(reelIdx, 0)}
                        />
                      </Box>

                      {/* Main Center Symbol (Row 1 - Highlighted Front Apex Crest) */}
                      <Box
                        sx={{
                          position: "relative",
                          zIndex: 5,
                          my: { xs: 0.25, sm: 0.5 },
                          filter: isThisReelSpinning ? "blur(4px)" : "none",
                          transform: isThisReelSpinning ? "scale(0.96)" : "scale(1)",
                          transition:
                            "filter 0.15s ease, transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                        }}
                      >
                        <ThreeSlotSymbolIcon
                          symbol={reelSymbols[1]}
                          size="lg"
                          isWinning={isSymbolOnWinningLine(reelIdx, 1)}
                        />
                      </Box>

                      {/* Bottom Symbol (Row 2 - Curved Lower Cylinder Slope) */}
                      <Box
                        sx={{
                          opacity: 0.9,
                          transform: "perspective(600px) rotateX(-15deg) scale(0.9)",
                          transformOrigin: "top center",
                          filter: isThisReelSpinning ? "blur(3px)" : "none",
                          transition: "filter 0.15s ease",
                          zIndex: 5,
                        }}
                      >
                        <ThreeSlotSymbolIcon
                          symbol={reelSymbols[2]}
                          size="md"
                          isWinning={isSymbolOnWinningLine(reelIdx, 2)}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {/* RIGHT SIDE PAYLINE INDICATOR STRIP (INTERACTIVE LINE 1..5 BADGES) */}
              <Box
                sx={{
                  width: { xs: 32, sm: 40 },
                  bgcolor: "#f8fafc",
                  borderLeft: "1px solid #e2e8f0",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-around",
                  py: 1.5,
                  alignItems: "center",
                  zIndex: 8,
                }}
              >
                {activePaylines.map((line) => {
                  const isSelected = selectedLines.includes(line.id);
                  const isHovered = hoveredLine === line.id;
                  const isWinning = winningLineResults.some((wl) => wl.lineId === line.id);

                  return (
                    <Box
                      key={`r-${line.id}`}
                      onClick={() => handleToggleLine(line.id)}
                      onMouseEnter={() => setHoveredLine(line.id)}
                      onMouseLeave={() => setHoveredLine(null)}
                      title={`${line.name} (${line.multiplier}x Multiplier): ${line.where}. Click to toggle.`}
                      sx={{
                        width: { xs: 26, sm: 34 },
                        height: { xs: 26, sm: 34 },
                        borderRadius: "8px",
                        bgcolor: isWinning
                          ? "#eab308"
                          : isSelected
                          ? line.badgeColor
                          : "#e2e8f0",
                        color: isSelected || isWinning ? "#ffffff" : "#94a3b8",
                        border: isWinning
                          ? "2px solid #fef08a"
                          : isSelected
                          ? "1.5px solid #ffffff"
                          : "1px dashed #cbd5e1",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        boxShadow: isWinning
                          ? "0 0 14px rgba(234, 179, 8, 0.9)"
                          : isSelected
                          ? `0 2px 8px ${line.color}55`
                          : "none",
                        transform: isHovered || isWinning ? "scale(1.15)" : "scale(1)",
                        transition: "all 0.15s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                        opacity: isSelected ? 1 : 0.45,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: "0.62rem", sm: "0.72rem" },
                          fontWeight: 900,
                          lineHeight: 1,
                        }}
                      >
                        {line.lineNum}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: "0.48rem", sm: "0.55rem" },
                          fontWeight: 800,
                          lineHeight: 1,
                          opacity: 0.95,
                          mt: "1px",
                        }}
                      >
                        {line.multiplier}x
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* 5. FLEXIBLE PAYLINE CONTROLS (PLAYER PAYLINE SELECTION) */}
          <Box
            sx={{
              mb: { xs: 1.5, sm: 2 },
              p: { xs: 1.25, sm: 1.75 },
              borderRadius: "14px",
              bgcolor: "#ffffff",
              border: "1.5px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
            }}
          >
            {/* Header: Status & Quick Select Buttons */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1}
              sx={{ mb: 1.25 }}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    sx={{
                      fontSize: { xs: "0.82rem", sm: "0.9rem" },
                      fontWeight: 800,
                      color: "#0f172a",
                      letterSpacing: "0.3px",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <span>🎯</span> ACTIVE PAYLINES
                  </Typography>
                  <Box
                    sx={{
                      px: 1,
                      py: "2px",
                      borderRadius: "6px",
                      bgcolor: selectedLines.length === activePaylines.length ? "#faf5ff" : "#f1f5f9",
                      border: `1px solid ${selectedLines.length === activePaylines.length ? "#d8b4fe" : "#cbd5e1"}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 800,
                        color: selectedLines.length === activePaylines.length ? "#8b5cf6" : "#475569",
                      }}
                    >
                      {selectedLines.length} / {activePaylines.length} Lines Active
                    </Typography>
                  </Box>
                </Stack>
                <Typography sx={{ fontSize: "0.68rem", color: "#64748b", mt: "2px" }}>
                  Total bet is {currency} {totalBetAmount.toFixed(2)}, split equally as Line Contribution ({currency} {betPerLine.toFixed(2)} / line). Select 1 to {activePaylines.length} lines to concentrate or spread your coverage!
                </Typography>
              </Box>

              {/* Quick Line Selector Controls: Single Line & All Lines / Max Bet */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectSingleLine}
                  sx={{
                    flex: { xs: 1, sm: "none" },
                    fontSize: "0.72rem",
                    fontWeight: 800,
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 1.5,
                    py: 0.5,
                    borderColor: selectedLines.length === 1 && selectedLines[0] === 1 ? "#8b5cf6" : "#cbd5e1",
                    color: selectedLines.length === 1 && selectedLines[0] === 1 ? "#7c3aed" : "#475569",
                    bgcolor: selectedLines.length === 1 && selectedLines[0] === 1 ? "#faf5ff" : "#ffffff",
                    "&:hover": {
                      borderColor: "#8b5cf6",
                      bgcolor: "#f5f3ff",
                    },
                  }}
                >
                  Single Line (L1)
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectAllLines}
                  sx={{
                    flex: { xs: 1, sm: "none" },
                    fontSize: "0.72rem",
                    fontWeight: 800,
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 1.5,
                    py: 0.5,
                    borderColor: selectedLines.length === activePaylines.length ? "#eab308" : "#cbd5e1",
                    color: selectedLines.length === activePaylines.length ? "#b45309" : "#475569",
                    bgcolor: selectedLines.length === activePaylines.length ? "#fefce8" : "#ffffff",
                    "&:hover": {
                      borderColor: "#eab308",
                      bgcolor: "#fef9c3",
                    },
                  }}
                >
                  All Lines / Max Bet
                </Button>
              </Stack>
            </Stack>

            {/* The Individual Line Selector Chips */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: `repeat(${Math.min(activePaylines.length, 5)}, 1fr)`,
                },
                gap: 1,
              }}
            >
              {activePaylines.map((line) => {
                const isSelected = selectedLines.includes(line.id);
                const isHovered = hoveredLine === line.id;
                const isWinning = winningLineResults.some((wl) => wl.lineId === line.id);

                return (
                  <Box
                    key={`chip-${line.id}`}
                    onClick={() => handleToggleLine(line.id)}
                    onMouseEnter={() => setHoveredLine(line.id)}
                    onMouseLeave={() => setHoveredLine(null)}
                    sx={{
                      p: 1,
                      borderRadius: "10px",
                      bgcolor: isWinning
                        ? "#fef9c3"
                        : isSelected
                        ? line.bgAlpha
                        : "#f8fafc",
                      border: isWinning
                        ? "2px solid #eab308"
                        : isSelected
                        ? `1.5px solid ${line.color}`
                        : "1px dashed #cbd5e1",
                      boxShadow: isWinning
                        ? "0 0 12px rgba(234, 179, 8, 0.4)"
                        : isSelected
                        ? `0 2px 8px ${line.color}33`
                        : "none",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      transform: isHovered || isWinning ? "scale(1.03)" : "scale(1)",
                      opacity: isSelected ? 1 : 0.55,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      "&:hover": {
                        opacity: 1,
                        borderColor: line.color,
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.6}>
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: "4px",
                          bgcolor: isSelected ? line.color : "#94a3b8",
                          color: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.68rem",
                          fontWeight: 900,
                        }}
                      >
                        {line.lineNum}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 800,
                          color: isSelected ? "#0f172a" : "#64748b",
                        }}
                      >
                        {line.shortLabel}
                      </Typography>
                      <Box
                        sx={{
                          px: 0.6,
                          py: "1px",
                          borderRadius: "4px",
                          bgcolor: isSelected ? `${line.color}22` : "#e2e8f0",
                          color: isSelected ? line.badgeColor : "#64748b",
                          fontSize: "0.68rem",
                          fontWeight: 900,
                        }}
                      >
                        {line.multiplier}x
                      </Box>
                    </Stack>
                    <Typography
                      sx={{
                        fontSize: "0.62rem",
                        color: isSelected ? "#475569" : "#94a3b8",
                        mt: "2px",
                        fontWeight: 600,
                      }}
                    >
                      {line.pattern}
                    </Typography>
                    <Box
                      sx={{
                        mt: "4px",
                        px: 0.75,
                        py: "1px",
                        borderRadius: "4px",
                        bgcolor: isSelected ? "#ede9fe" : "#f1f5f9",
                        color: isSelected ? "#6d28d9" : "#94a3b8",
                        fontSize: "0.62rem",
                        fontWeight: 800,
                        fontFamily: "monospace",
                      }}
                    >
                      {isSelected ? `${currency} ${betPerLine.toFixed(2)}` : "Inactive"}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* 6. LIVE PAYLINE WIN / MISSED WIN TICKER ANNOUNCEMENT */}
          {(winningLineResults.length > 0 || missedLineResults.length > 0) && !isSpinning && (
            <Box
              sx={{
                mb: { xs: 1.5, sm: 2 },
                p: 1.25,
                borderRadius: "10px",
                bgcolor: winningLineResults.length > 0 ? "#f0fdf4" : "#fffbeb",
                border: winningLineResults.length > 0 ? "1.5px solid #86efac" : "1.5px solid #fde68a",
                boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                textAlign: "center",
              }}
            >
              {winningLineResults.length > 0 ? (
                <Stack spacing={0.5}>
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: 900,
                      color: "#166534",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.75,
                    }}
                  >
                    <span>🎉</span> WINNING PAYLINE PAYS: {currency} {currentWin}!
                  </Typography>
                  <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
                    {winningLineResults.map((wl, i) => (
                      <Box
                        key={`win-badge-${i}`}
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: "6px",
                          bgcolor: "#dcfce7",
                          border: "1px solid #bbf7d0",
                          fontSize: "0.72rem",
                          fontWeight: 800,
                          color: "#15803d",
                        }}
                      >
                        {wl.payline.name} ({wl.lineMultiplier}x): 3x {wl.symbol.label} = {currency} {wl.baseLineWin.toFixed(2)}
                        {wl.hasJackpot && ` + ${wl.jackpotTier.toUpperCase()} JACKPOT (${currency} ${wl.jackpotCash.toFixed(2)})`}
                        {wl.spinsAwarded > 0 && ` + ${wl.spinsAwarded} FREE SPINS`}
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              ) : (
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: "#92400e",
                  }}
                >
                  ℹ️ 3-of-a-kind landed on Line {missedLineResults[0]?.lineNum} ({missedLineResults[0]?.lineMultiplier}x), but this line was unselected (Rule 10.3: Only active lines pay).
                </Typography>
              )}
            </Box>
          )}

          {/* 7. DASHBOARD BAR (BALANCE | ACTIVE LINES | BET/LINE | TOTAL BET | WIN) */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(5, 1fr)" },
              gap: { xs: 1, sm: 1.5 },
              p: { xs: 1.25, sm: 1.75 },
              borderRadius: "12px",
              bgcolor: "#ffffff",
              border: "1.5px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
              mb: { xs: 2, sm: 2.5 },
            }}
          >
            {/* BALANCE */}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.7rem" },
                  fontWeight: 800,
                  color: "#64748b",
                  letterSpacing: "0.5px",
                }}
              >
                BALANCE
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "0.98rem" },
                  fontWeight: 900,
                  color: "#0f172a",
                  fontFamily: "monospace",
                }}
              >
                {currency} {parseFloat(balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>

            {/* ACTIVE LINES */}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.7rem" },
                  fontWeight: 800,
                  color: "#64748b",
                  letterSpacing: "0.5px",
                }}
              >
                ACTIVE LINES
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "0.98rem" },
                  fontWeight: 900,
                  color: "#8b5cf6",
                  fontFamily: "monospace",
                }}
              >
                {selectedLines.length} / {activePaylines.length}
              </Typography>
            </Box>

            {/* LINE CONTRIBUTION */}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.7rem" },
                  fontWeight: 800,
                  color: "#64748b",
                  letterSpacing: "0.5px",
                }}
              >
                LINE CONTRIBUTION
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "0.98rem" },
                  fontWeight: 900,
                  color: "#0f172a",
                  fontFamily: "monospace",
                  mt: 0.25,
                }}
              >
                {currency} {betPerLine.toFixed(2)}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.58rem",
                  fontWeight: 700,
                  color: "#8b5cf6",
                  lineHeight: 1,
                  mt: "2px",
                }}
              >
                {`${(100 / Math.max(1, selectedLines.length)).toFixed(0)}% per line`}
              </Typography>
            </Box>

            {/* TOTAL BET (WITH USER INPUT / STEPPER / MAX AVAILABLE) */}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.7rem" },
                  fontWeight: 800,
                  color: "#64748b",
                  letterSpacing: "0.5px",
                }}
              >
                TOTAL BET {isBetFixed ? "(FIXED)" : `(MAX ${maxBet})`}
              </Typography>

              {isBetFixed ? (
                <Typography
                  sx={{
                    fontSize: { xs: "0.78rem", sm: "0.98rem" },
                    fontWeight: 900,
                    color: "#d97706",
                    fontFamily: "monospace",
                    mt: 0.25,
                  }}
                >
                  {currency} {totalBetAmount.toFixed(2)}
                </Typography>
              ) : (
                <Box sx={{ mt: 0.25 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={0.5}
                  >
                    <Box
                      onClick={handleDecreaseBet}
                      sx={{
                        width: { xs: 20, sm: 24 },
                        height: { xs: 20, sm: 24 },
                        borderRadius: "5px",
                        bgcolor: "#f1f5f9",
                        border: "1px solid #cbd5e1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: isSpinning || spinLoading ? "not-allowed" : "pointer",
                        color: "#334155",
                        "&:hover": { bgcolor: "#e2e8f0" },
                        "&:active": { transform: "scale(0.92)" },
                      }}
                    >
                      <RemoveIcon sx={{ fontSize: { xs: 13, sm: 15 } }} />
                    </Box>

                    <Box
                      component="input"
                      type="number"
                      value={betInputText}
                      onChange={handleBetInputChange}
                      onBlur={handleBetInputBlur}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleBetInputBlur();
                      }}
                      step={stepSize}
                      min={minBet}
                      max={maxBet}
                      disabled={isSpinning || spinLoading}
                      sx={{
                        width: { xs: 58, sm: 72 },
                        py: "1px",
                        px: "2px",
                        textAlign: "center",
                        fontSize: { xs: "0.76rem", sm: "0.92rem" },
                        fontWeight: 900,
                        color: "#d97706",
                        fontFamily: "monospace",
                        bgcolor: "#fffbeb",
                        border: "1.5px solid #fde68a",
                        borderRadius: "5px",
                        outline: "none",
                        "&:focus": {
                          borderColor: "#f59e0b",
                          boxShadow: "0 0 0 2px rgba(245, 158, 11, 0.2)",
                        },
                      }}
                    />

                    <Box
                      onClick={handleIncreaseBet}
                      sx={{
                        width: { xs: 20, sm: 24 },
                        height: { xs: 20, sm: 24 },
                        borderRadius: "5px",
                        bgcolor: "#f1f5f9",
                        border: "1px solid #cbd5e1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: isSpinning || spinLoading ? "not-allowed" : "pointer",
                        color: "#334155",
                        "&:hover": { bgcolor: "#e2e8f0" },
                        "&:active": { transform: "scale(0.92)" },
                      }}
                    >
                      <AddIcon sx={{ fontSize: { xs: 13, sm: 15 } }} />
                    </Box>
                  </Stack>

                  {/* Quick MIN / MAX shortcuts */}
                  <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ mt: "3px" }}>
                    <Typography
                      component="span"
                      onClick={handleSetMinBet}
                      sx={{
                        fontSize: "0.58rem",
                        fontWeight: 800,
                        color: "#64748b",
                        cursor: isSpinning || spinLoading ? "not-allowed" : "pointer",
                        px: "4px",
                        py: "1px",
                        borderRadius: "3px",
                        bgcolor: "#f1f5f9",
                        "&:hover": { color: "#0f172a", bgcolor: "#e2e8f0" },
                      }}
                    >
                      MIN ({minBet})
                    </Typography>
                    <Typography
                      component="span"
                      onClick={handleSetMaxBet}
                      sx={{
                        fontSize: "0.58rem",
                        fontWeight: 800,
                        color: "#b45309",
                        cursor: isSpinning || spinLoading ? "not-allowed" : "pointer",
                        px: "4px",
                        py: "1px",
                        borderRadius: "3px",
                        bgcolor: "#fef3c7",
                        "&:hover": { color: "#78350f", bgcolor: "#fde68a" },
                      }}
                    >
                      MAX ({maxBet})
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Box>

            {/* WIN AMOUNT */}
            <Box sx={{ textAlign: "center", gridColumn: { xs: "span 2", sm: "span 1" } }}>
              <Typography
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.7rem" },
                  fontWeight: 800,
                  color: "#64748b",
                  letterSpacing: "0.5px",
                }}
              >
                CURRENT WIN
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.78rem", sm: "0.98rem" },
                  fontWeight: 900,
                  color: parseFloat(currentWin) > 0 ? "#16a34a" : "#0f172a",
                  fontFamily: "monospace",
                }}
              >
                {currency} {currentWin}
              </Typography>
            </Box>
          </Box>

          {/* 6. MAIN ACTION BAR (PAYTABLE | BIG 3D SPIN | AUTOPLAY) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: { xs: 0.5, sm: 1.5 },
              gap: { xs: 2, sm: 4 },
            }}
          >
            {/* PAYTABLE BUTTON */}
            <Box
              onClick={() => {
                slotAudio.playClick();
                setPaytableOpen(true);
              }}
              sx={{
                flex: 1,
                maxWidth: { xs: 140, sm: 180 },
                height: { xs: 52, sm: 58 },
                borderRadius: "14px",
                background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                border: "1.5px solid #a78bfa",
                boxShadow: "0 4px 12px rgba(139, 92, 246, 0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": { filter: "brightness(1.1)" },
                "&:active": { transform: "scale(0.96)" },
              }}
            >
              <FormatListBulletedIcon sx={{ color: "#ffffff", fontSize: { xs: 20, sm: 22 } }} />
              <Typography
                sx={{
                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                  fontSize: { xs: "0.8rem", sm: "0.98rem" },
                  fontWeight: 900,
                  color: "#ffffff",
                  letterSpacing: "1px",
                }}
              >
                PAYTABLE
              </Typography>
            </Box>

            {/* QUICK BET MINUS */}
            {!isBetFixed && (
              <Box
                onClick={handleDecreaseBet}
                title={`Decrease bet by ${stepSize} ${currency}`}
                sx={{
                  width: { xs: 44, sm: 52 },
                  height: { xs: 52, sm: 58 },
                  borderRadius: "14px",
                  bgcolor: "#f8fafc",
                  border: "1.5px solid #cbd5e1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isSpinning || spinLoading ? "not-allowed" : "pointer",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                  transition: "all 0.15s ease",
                  "&:hover": { bgcolor: "#f1f5f9", borderColor: "#94a3b8" },
                  "&:active": { transform: "scale(0.92)" },
                }}
              >
                <RemoveIcon sx={{ color: "#0f172a", fontSize: { xs: 24, sm: 28 } }} />
              </Box>
            )}

            {/* BIG 3D RED SPIN BUTTON */}
            <Box
              onClick={handleSpin}
              sx={{
                width: { xs: 92, sm: 110 },
                height: { xs: 92, sm: 110 },
                flexShrink: 0,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #fef08a 0%, #eab308 50%, #854d0e 100%)",
                p: "5px",
                boxShadow:
                  "0 6px 20px rgba(239, 68, 68, 0.4), 0 2px 8px rgba(0, 0, 0, 0.15), inset 0 2px 4px rgba(255,255,255,0.9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isSpinning || spinLoading ? "not-allowed" : "pointer",
                transform: isSpinning || spinLoading ? "scale(0.95)" : "scale(1)",
                transition: "all 0.15s ease",
                "&:hover": {
                  boxShadow:
                    "0 8px 28px rgba(239, 68, 68, 0.6), 0 2px 10px rgba(0, 0, 0, 0.2)",
                },
                "&:active": {
                  transform: "scale(0.92)",
                },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  background: isSpinning
                    ? "radial-gradient(circle at 35% 30%, #f87171 0%, #dc2626 55%, #7f1d1d 100%)"
                    : "radial-gradient(circle at 35% 30%, #ef4444 0%, #b91c1c 55%, #450a0a 100%)",
                  border: "2.5px solid #ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "inset 0 4px 10px rgba(255,255,255,0.6), inset 0 -4px 10px rgba(0,0,0,0.8)",
                  animation: isSpinning ? "spinButtonPulse 0.5s infinite alternate" : "none",
                  "@keyframes spinButtonPulse": {
                    "0%": { filter: "brightness(1)" },
                    "100%": { filter: "brightness(1.3)" },
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Impact', 'Arial Black', sans-serif",
                    fontSize: { xs: "1.55rem", sm: "1.95rem" },
                    fontWeight: 900,
                    color: "#ffffff",
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    textShadow:
                      "0 2px 4px rgba(0,0,0,0.9), 0 0 8px rgba(254, 240, 138, 0.8)",
                    transform: "translateY(-1px)",
                  }}
                >
                    {spinLoading ? "WAIT" : isSpinning ? "..." : "SPIN"}
                </Typography>
              </Box>
            </Box>

            {/* QUICK BET PLUS */}
            {!isBetFixed && (
              <Box
                onClick={handleIncreaseBet}
                title={`Increase bet by ${stepSize} ${currency}`}
                sx={{
                  width: { xs: 44, sm: 52 },
                  height: { xs: 52, sm: 58 },
                  borderRadius: "14px",
                  bgcolor: "#f8fafc",
                  border: "1.5px solid #cbd5e1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isSpinning || spinLoading ? "not-allowed" : "pointer",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                  transition: "all 0.15s ease",
                  "&:hover": { bgcolor: "#f1f5f9", borderColor: "#94a3b8" },
                  "&:active": { transform: "scale(0.92)" },
                }}
              >
                <AddIcon sx={{ color: "#0f172a", fontSize: { xs: 24, sm: 28 } }} />
              </Box>
            )}

            {/* AUTOPLAY BUTTON */}
            <Box
              onClick={handleToggleAutoPlay}
              sx={{
                flex: 1,
                maxWidth: { xs: 140, sm: 180 },
                height: { xs: 52, sm: 58 },
                borderRadius: "14px",
                background: isAutoSpinning
                  ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                  : "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                border: isAutoSpinning ? "1.5px solid #f87171" : "1.5px solid #60a5fa",
                boxShadow: isAutoSpinning
                  ? "0 4px 12px rgba(239, 68, 68, 0.35)"
                  : "0 4px 12px rgba(59, 130, 246, 0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": { filter: "brightness(1.1)" },
                "&:active": { transform: "scale(0.96)" },
              }}
            >
              <AutorenewIcon
                sx={{
                  color: "#ffffff",
                  fontSize: { xs: 20, sm: 22 },
                  animation: isAutoSpinning ? "spinIcon 1s linear infinite" : "none",
                  "@keyframes spinIcon": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
              <Typography
                sx={{
                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                  fontSize: { xs: "0.8rem", sm: "0.98rem" },
                  fontWeight: 900,
                  color: "#ffffff",
                  letterSpacing: "1px",
                }}
              >
                {isAutoSpinning ? "STOP" : "AUTOPLAY"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* 7. MODALS & POPUPS */}
      <ThreeSlotPaytableModal
        open={paytableOpen}
        onClose={() => setPaytableOpen(false)}
        activeCategory={category}
        currency={currency}
        paylines={activePaylines}
      />
      <ThreeSlotDepositModal
        open={depositOpen}
        onClose={() => setDepositOpen(false)}
        currentBalance={balance}
        onDepositSuccess={(newBal) => setBalance(newBal)}
        currency={currency}
      />
      <ThreeSlotWinModal
        open={winModalOpen}
        onClose={() => setWinModalOpen(false)}
        result={lastResult}
        currencySymbol={currency}
        betAmount={betAmount}
        onSpinAgain={() => {
          setWinModalOpen(false);
          if (!isSpinning && !spinLoading) {
            handleSpin();
          }
        }}
      />
      <FailedPopup
        open={failedModal.open}
        show={failedModal.open}
        setShowModal={(open) => setFailedModal((prev) => ({ ...prev, open }))}
        onClose={() => setFailedModal((prev) => ({ ...prev, open: false }))}
        title={failedModal.title}
        message={failedModal.message}
        description={failedModal.description}
        btnName="Close"
      />
    </Box>
  );
};

export default ThreeSlotMachineDetailCard;